function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo, useState, useCallback } from 'react';
import { useDeprecate, useOnClickOutside } from '@adsk/alloy-react-helpers';
import usePopper from './usePopper';
import useVisibility from './useVisibility';
import { PLACEMENTS } from '../consts';
function useOverlay(_ref) {
  let {
    show,
    delayShow,
    delayHide,
    onHide,
    onShow,
    placement: placementProp,
    target: targetProp,
    overlayRef,
    rootClose = true,
    offset,
    layoutDependency,
    shiftArgs,
    flipArgs,
    hideArgs,
    isOverlayConditionallyRendered = true
  } = _ref;
  useDeprecate(!!layoutDependency, 'useOverlay', 'layoutDependency', '"No longer required"');
  const toggle = useVisibility({
    show,
    onHide,
    onShow,
    delayShow,
    delayHide
  });
  const [targetElement, setTargetElement] = useState((targetProp === null || targetProp === void 0 ? void 0 : targetProp.current) || null);
  const [overlayElement, setOverlayElement] = useState((overlayRef === null || overlayRef === void 0 ? void 0 : overlayRef.current) || null);
  const [arrowElement, setArrowElement] = useState(null);

  /** Ideally users should use the target ref returned from `useOverlay`,
   * but in the case that they provide a `target`, we need to keep our state up to date with its current value
   */
  if (targetProp && targetProp.current !== targetElement) {
    setTargetElement(targetProp.current);
  }
  const {
    placement: popperPlacement,
    popoverStyles,
    arrowStyles,
    refs
  } = usePopper({
    placement: placementProp,
    targetElement,
    overlayElement,
    arrowElement,
    offset,
    shiftArgs,
    flipArgs,
    hideArgs,
    show: toggle.show,
    isOverlayConditionallyRendered
  });
  const finalTargetRef = useCallback(instance => {
    if (!instance) {
      return;
    }
    setTargetElement(instance);
    if (targetProp) {
      targetProp.current = instance;
    }
    refs.setReference(instance);
  }, [targetProp, refs]);
  const finalOverlayRef = useCallback(instance => {
    if (!instance) {
      return;
    }

    // only update overlayElement if it's not the same as the instance
    // this fixes a very difficult to find Maximum update depth exceeded error
    if (overlayElement !== instance) {
      setOverlayElement(instance);
    }
    refs.setFloating(instance);
    if (overlayRef) {
      overlayRef.current = instance;
    }
  }, [refs, overlayRef, overlayElement]);
  const notUndefined = x => x !== undefined;
  const refsToIgnore = useMemo(() => [targetProp, refs.floating, overlayRef].filter(notUndefined), [targetProp, refs.floating, overlayRef]);

  // Handle close on click outside
  const enableCloseOnClickOutside = rootClose && toggle.show;
  useOnClickOutside(enableCloseOnClickOutside, toggle.handleHide, refsToIgnore);
  const targetProps = useMemo(() => ({
    ref: finalTargetRef
  }), [finalTargetRef]);
  const overlayProps = useMemo(() => ({
    ref: finalOverlayRef,
    style: popoverStyles,
    placement: popperPlacement
  }), [finalOverlayRef, popoverStyles, popperPlacement]);
  const arrowProps = useMemo(() => ({
    ref: setArrowElement,
    style: arrowStyles,
    placement: popperPlacement
  }), [arrowStyles, popperPlacement]);
  return _objectSpread(_objectSpread({}, toggle), {}, {
    placement: popperPlacement,
    targetProps,
    overlayProps,
    arrowProps
  });
}
useOverlay.PLACEMENTS = PLACEMENTS;
export default useOverlay;
//# sourceMappingURL=useOverlay.js.map